/**
 * 矢量图形路径生成器
 *
 * 所有路径坐标归一化到 0–100 × 0–100 的 viewBox 空间，
 * 渲染时通过 SVG viewBox + preserveAspectRatio="none" 自动缩放至元素尺寸。
 */

/**
 * 心形路径 —— 采用 Heroicons 算法，等比缩放至 0–100 × 0–100 viewBox。
 * 原始路径来自 24×24 viewBox，缩放系数 100/24 ≈ 4.1667。
 *
 * 原始 SVG（24px）：
 *   M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733
 *   -.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25
 *   c0 7.22 9 12 9 12s9-4.78 9-12Z
 */
export function heartPath(): string {
  return (
    'M87.5,34.375 ' +
    'c0,-10.354,-8.746,-18.75,-19.533,-18.75 ' +
    'c-8.063,0,-14.988,4.692,-17.967,11.388 ' +
    'c-2.979,-6.696,-9.904,-11.388,-17.971,-11.388 ' +
    'C21.25,15.625,12.5,24.021,12.5,34.375 ' +
    'c0,30.083,37.5,50,37.5,50 ' +
    's37.5,-19.917,37.5,-50 ' +
    'Z'
  );
}

/**
 * 正多边形路径（外接圆半径 = 50，顶点完整触及 viewBox 边缘）
 * @param sides 边数，建议 3–12
 */
export function polygonPath(sides: number): string {
  const n = Math.max(3, Math.round(sides));
  const cx = 50;
  const cy = 50;
  const r = 50;               // 填满 viewBox，描边由 SVG overflow:visible 溢出显示
  const startAngle = -Math.PI / 2;  // 顶点朝上

  const points: string[] = [];
  for (let i = 0; i < n; i++) {
    const angle = startAngle + (2 * Math.PI * i) / n;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    points.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(3)} ${y.toFixed(3)}`);
  }
  return points.join(' ') + ' Z';
}

/**
 * 星形路径（外接圆半径 = 50，角尖完整触及 viewBox 边缘）
 * @param points  角数，建议 4–12
 * @param innerRatio  内外径比，0.1–0.9，值越小星越尖锐
 */
export function starPath(points: number, innerRatio: number): string {
  const n = Math.max(4, Math.round(points));
  const ratio = Math.max(0.1, Math.min(0.9, innerRatio));
  const cx = 50;
  const cy = 50;
  const outerR = 50;          // 填满 viewBox，描边由 SVG overflow:visible 溢出显示
  const innerR = outerR * ratio;
  const startAngle = -Math.PI / 2;

  const segments: string[] = [];
  for (let i = 0; i < n * 2; i++) {
    const angle = startAngle + (Math.PI * i) / n;
    const r = i % 2 === 0 ? outerR : innerR;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    segments.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(3)} ${y.toFixed(3)}`);
  }
  return segments.join(' ') + ' Z';
}

/** stroke-dasharray 字符串（坐标已在 viewBox 中，按相对单位给出） */
export function getDashArray(
  style: 'solid' | 'dashed' | 'dotted' | undefined,
  strokeWidth: number
): string | undefined {
  if (style === 'dashed') return `${strokeWidth * 4} ${strokeWidth * 2}`;
  if (style === 'dotted') return `${strokeWidth} ${strokeWidth}`;
  return undefined;
}

/**
 * 根据 VectorElement 参数返回对应 SVG path d 字符串
 */
export function getVectorPath(
  shape: 'heart' | 'polygon' | 'star' | 'path',
  opts: {
    polygonSides?: number;
    starPoints?: number;
    starInnerRatio?: number;
    pathData?: string;
  }
): string {
  switch (shape) {
    case 'heart':
      return heartPath();
    case 'polygon':
      return polygonPath(opts.polygonSides ?? 6);
    case 'star':
      return starPath(opts.starPoints ?? 5, opts.starInnerRatio ?? 0.45);
    case 'path':
      return opts.pathData ?? '';
    default:
      return '';
  }
}
